import { Interpolation, Theme } from '@emotion/react';
import {
  AppointmentStatus,
  AppointmentConfirmationStatus,
  AppointmentContactType,
  AtRiskStatus,
  ScheduleRequestReviewStatus,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { i18next } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, ChipProps } from '@frontend/design-system';
import { SCHEDULE_REQUEST_STATUS_LABELS } from '../../constants';

interface StatusChipProps extends Omit<ChipProps, 'children'> {
  status: string;
  css?: Interpolation<Theme>;
}

const formatEnumLabel = (value: string): string => {
  const prefixes = ['APPOINTMENT_STATUS_', 'APPOINTMENT_CNF_STATUS_'];
  let label = value;
  for (const prefix of prefixes) {
    if (label.startsWith(prefix)) {
      label = label.slice(prefix.length);
      break;
    }
  }
  if (label === 'REPEAT_NSR') {
    return i18next.t('Repeat No-Show', { ns: 'analytics' });
  }
  return label
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const APPOINTMENT_STATUS_MAPPING: Record<
  string,
  { icon: IconName; color: string; backgroundColor: string; labelKey: string }
> = {
  [AppointmentStatus.APPOINTMENT_STATUS_CANCELLED]: {
    icon: 'block-small',
    color: theme.colorsV2.red70(),
    backgroundColor: theme.colorsV2.red50(25),
    labelKey: i18next.t('Cancelled', { ns: 'analytics' }),
  },
  [AppointmentStatus.APPOINTMENT_STATUS_NSR]: {
    icon: 'user-x-small',
    color: theme.colorsV2.tangerine70(),
    backgroundColor: theme.colorsV2.tangerine50(25),
    labelKey: i18next.t('No Show', { ns: 'analytics' }),
  },
  [AppointmentStatus.APPOINTMENT_STATUS_COMPLETED]: {
    icon: 'check-small',
    color: theme.colorsV2.green70(),
    backgroundColor: theme.colorsV2.green50(25),
    labelKey: i18next.t('Completed', { ns: 'analytics' }),
  },
};

const CONFIRMATION_STATUS_MAPPING: Record<
  string,
  { icon: IconName; color: string; backgroundColor: string; labelKey: string }
> = {
  [AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_CONFIRMED]: {
    icon: 'calendar-confirmed-small',
    color: theme.colorsV2.blue70(),
    backgroundColor: theme.colorsV2.blue10(),
    labelKey: i18next.t('Confirmed', { ns: 'analytics' }),
  },
  [AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNCONFIRMED]: {
    icon: 'pending-small',
    color: theme.colors.warning70,
    backgroundColor: theme.colors.warning10,
    labelKey: i18next.t('Unconfirmed', { ns: 'analytics' }),
  },
};

export const AppointmentStatusChip = ({ status, css: customCss, ...props }: StatusChipProps) => {
  const mapping = APPOINTMENT_STATUS_MAPPING[status];

  if (mapping) {
    return (
      <Chip
        leftElement={<Icon name={mapping.icon as any} />}
        css={[
          {
            color: mapping.color,
            backgroundColor: mapping.backgroundColor,
            borderColor: 'transparent',
            svg: {
              color: mapping.color,
              fill: mapping.color,
            },
          },
          customCss,
        ]}
        {...props}
      >
        {mapping.labelKey}
      </Chip>
    );
  }

  // Fallback for other status values
  const label = formatEnumLabel(status);

  if (label === 'Confirmed') {
    return (
      <AppointmentConfirmationStatusChip
        status={AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_CONFIRMED}
        css={customCss}
        {...props}
      />
    );
  }
  return (
    <Chip variant='neutral' css={customCss} {...props}>
      {label}
    </Chip>
  );
};

export const AppointmentConfirmationStatusChip = ({ status, css: customCss, ...props }: StatusChipProps) => {
  const mapping = CONFIRMATION_STATUS_MAPPING[status];

  if (mapping) {
    return (
      <Chip
        leftElement={<Icon name={mapping.icon as any} />}
        css={[
          {
            color: mapping.color,
            backgroundColor: mapping.backgroundColor,
            borderColor: 'transparent',
            svg: {
              color: mapping.color,
              fill: mapping.color,
            },
          },
          customCss,
        ]}
        maxWidth='unset'
        {...props}
      >
        {mapping.labelKey}
      </Chip>
    );
  }

  // Fallback for unclassified values (like unspecified)
  const label = formatEnumLabel(status);
  return (
    <Chip variant='neutral' css={customCss} {...props}>
      {label}
    </Chip>
  );
};

const NO_SHOW_RISK_STATUS_MAPPING: Record<
  string,
  { icon: IconName; color: string; backgroundColor: string; labelKey: string }
> = {
  [AtRiskStatus.AT_RISK_STATUS_REPEAT_CANCEL]: {
    icon: 'block-small',
    color: theme.colorsV2.red70(),
    backgroundColor: theme.colorsV2.red50(25),
    labelKey: i18next.t('Repeat Cancelled', { ns: 'analytics' }),
  },
  [AtRiskStatus.AT_RISK_STATUS_REPEAT_NSR]: {
    icon: 'user-x-small',
    color: theme.colorsV2.tangerine70(),
    backgroundColor: theme.colorsV2.tangerine50(25),
    labelKey: i18next.t('Repeat No Show', { ns: 'analytics' }),
  },
  [AtRiskStatus.AT_RISK_STATUS_REPEAT_BOTH]: {
    icon: 'plus-small',
    color: theme.colorsV2.tangerine70(),
    backgroundColor: theme.colorsV2.tangerine50(25),
    labelKey: i18next.t('Repeat Confirmed & No Show', { ns: 'analytics' }),
  },
};

export const AppointmentNoShowRiskChip = ({ status, css: customCss, ...props }: StatusChipProps) => {
  const mapping = NO_SHOW_RISK_STATUS_MAPPING[status];

  if (mapping) {
    return (
      <Chip
        leftElement={<Icon name={mapping.icon as any} />}
        css={[
          {
            color: mapping.color,
            backgroundColor: mapping.backgroundColor,
            borderColor: 'transparent',
            svg: {
              color: mapping.color,
              fill: mapping.color,
            },
          },
          customCss,
        ]}
        maxWidth='180px'
        {...props}
      >
        {mapping.labelKey}
      </Chip>
    );
  }

  // Fallback for unclassified values (like unspecified)
  const label = formatEnumLabel(status);
  return (
    <Chip variant='neutral' css={customCss} {...props}>
      {label}
    </Chip>
  );
};

const PATIENT_TYPE_RAW_STRING_ALIASES: Record<string, AppointmentContactType> = {
  'Existing Patient': AppointmentContactType.APPOINTMENT_EXISTING_PATIENTS,
  'New Patient': AppointmentContactType.APPOINTMENT_NEW_PATIENTS,
};

const PATIENT_TYPE_MAPPING: Record<string, { label: string; variant: ChipProps['variant'] }> = {
  [AppointmentContactType.APPOINTMENT_EXISTING_PATIENTS]: {
    label: i18next.t('Existing Patient', { ns: 'analytics' }),
    variant: 'primary',
  },
  [AppointmentContactType.APPOINTMENT_NEW_PATIENTS]: {
    label: i18next.t('New Patient', { ns: 'analytics' }),
    variant: 'warn',
  },
};

export const PatientTypeChip = ({
  type,
  css: customCss,
  ...props
}: {
  type: string | AppointmentContactType;
  css?: any;
} & Omit<ChipProps, 'children'>) => {
  const normalizedType = PATIENT_TYPE_RAW_STRING_ALIASES[type] ?? type;
  const chipProps = PATIENT_TYPE_MAPPING[normalizedType] ?? { label: type, variant: 'neutral' };

  return (
    <Chip variant={chipProps.variant} css={customCss} {...props}>
      {chipProps.label}
    </Chip>
  );
};

const SCHEDULE_REQUEST_STATUS_VARIANT_MAPPING: Record<string, ChipProps['variant']> = {
  [ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_PENDING]: 'tangerine',
  [ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_ACCEPTED]: 'success',
  [ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_REJECTED]: 'rose',
  [ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_MESSAGED]: 'primary',
  [ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_CALLED]: 'primary',
  [ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_DONE]: 'done',
  [ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_DISMISSED]: 'neutral',
  [ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_DELETED]: 'critical',
};

export const ScheduleRequestStatusChip = ({
  status,
  css: customCss,
  ...props
}: StatusChipProps & { status: ScheduleRequestReviewStatus }) => {
  const label = SCHEDULE_REQUEST_STATUS_LABELS[status] ?? formatEnumLabel(status);
  const variant = SCHEDULE_REQUEST_STATUS_VARIANT_MAPPING[status] ?? 'neutral';

  return (
    <Chip variant={variant} css={customCss} {...props}>
      {label}
    </Chip>
  );
};
